import { AlertTriangle, CheckCircle2, Clock, Users } from 'lucide-react';
import {
  getSendPipelineSummary,
  type SendPipelinePhase,
  type SendPipelineTone,
} from '../../lib/newsletterSchedule';

interface NewsletterSendPipelineBannerProps {
  phase: SendPipelinePhase;
  status: string;
  scheduledFor: string | null;
  onQueueRecipients: () => void;
  queueDisabled?: boolean;
  queueDisabledReason?: string;
}

function bannerShellClass(tone: SendPipelineTone): string {
  switch (tone) {
    case 'queued':
      return 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30';
    case 'overdue-draft':
    case 'overdue-scheduled':
      return 'border-red-300 dark:border-red-800 bg-red-50/90 dark:bg-red-950/30';
    case 'draft':
      return 'border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-900/40';
    case 'scheduled':
      return 'border-violet-300 dark:border-violet-700 bg-violet-50/90 dark:bg-violet-950/25';
    case 'on-hold':
      return 'border-amber-300 dark:border-amber-700 bg-amber-50/90 dark:bg-amber-950/25';
    default:
      return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40';
  }
}

function titleClass(tone: SendPipelineTone): string {
  switch (tone) {
    case 'queued':
      return 'font-semibold text-emerald-950 dark:text-emerald-100';
    case 'overdue-draft':
    case 'overdue-scheduled':
      return 'font-semibold text-red-900 dark:text-red-100';
    case 'draft':
      return 'font-semibold text-slate-900 dark:text-slate-100';
    case 'scheduled':
      return 'font-semibold text-violet-950 dark:text-violet-100';
    case 'on-hold':
      return 'font-semibold text-amber-950 dark:text-amber-100';
    default:
      return 'font-semibold text-gray-900 dark:text-white';
  }
}

function bodyClass(tone: SendPipelineTone): string {
  switch (tone) {
    case 'queued':
      return 'mt-1 text-emerald-950 dark:text-emerald-100';
    case 'overdue-draft':
    case 'overdue-scheduled':
      return 'mt-1 text-red-800/90 dark:text-red-200/90';
    case 'draft':
      return 'mt-1 text-slate-700/90 dark:text-slate-300/90';
    case 'scheduled':
      return 'mt-1 text-violet-900/90 dark:text-violet-200/90';
    case 'on-hold':
      return 'mt-1 text-amber-900/90 dark:text-amber-200/90';
    default:
      return 'mt-1 text-gray-700 dark:text-gray-300';
  }
}

function noteClass(tone: SendPipelineTone): string {
  if (tone === 'overdue-draft' || tone === 'overdue-scheduled') {
    return 'mt-2 text-xs text-red-700/90 dark:text-red-200/80';
  }
  if (tone === 'scheduled') {
    return 'mt-2 text-xs text-violet-800/90 dark:text-violet-200/80';
  }
  return 'mt-2 text-xs text-gray-600 dark:text-gray-400';
}

function StatusIcon({ tone }: { tone: SendPipelineTone }) {
  if (tone === 'queued') {
    return <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />;
  }
  if (tone === 'overdue-draft' || tone === 'overdue-scheduled') {
    return <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />;
  }
  return (
    <Clock
      className={`w-5 h-5 shrink-0 mt-0.5 ${
        tone === 'draft'
          ? 'text-slate-500 dark:text-slate-400'
          : tone === 'scheduled'
            ? 'text-violet-600 dark:text-violet-400'
            : 'text-amber-600 dark:text-amber-400'
      }`}
    />
  );
}

export default function NewsletterSendPipelineBanner({
  phase,
  status,
  scheduledFor,
  onQueueRecipients,
  queueDisabled = false,
  queueDisabledReason,
}: NewsletterSendPipelineBannerProps) {
  const summary = getSendPipelineSummary({ phase, status, scheduledFor });
  if (!summary) return null;

  const showQueueButton =
    summary.tone !== 'queued' && summary.tone !== 'on-hold';

  return (
    <div className={`mb-4 rounded-xl border px-4 py-3 sm:px-5 sm:py-4 ${bannerShellClass(summary.tone)}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
        <div className="flex gap-3 min-w-0 flex-1">
          <StatusIcon tone={summary.tone} />
          <div className="min-w-0 text-sm">
            <p className={titleClass(summary.tone)}>{summary.headline}</p>
            <p className={bodyClass(summary.tone)}>{summary.detail}</p>
            {summary.autoQueueNote ? (
              <p className={noteClass(summary.tone)}>{summary.autoQueueNote}</p>
            ) : null}
          </div>
        </div>
        {showQueueButton ? (
          <button
            type="button"
            onClick={onQueueRecipients}
            disabled={queueDisabled}
            title={queueDisabled ? queueDisabledReason : undefined}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 shrink-0"
          >
            <Users className="w-4 h-4" />
            Queue recipients…
          </button>
        ) : null}
      </div>
    </div>
  );
}
